import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  ElementRef,
  HostBinding,
  Input,
  Output,
  Signal,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { CommonPerm } from '@enum/common/permission/common-perm.enum';
import { TenantPerm } from '@enum/tenant/permission/tenant-perm.enum';
import { PermissionService } from '@shared/services/permission.service';
import { CenaOrbitalComponent } from './cena/cena-orbital.component';
import { FaseDaCena } from './cena/cena.model';
import { ContadorAnimadoDirective } from './cena/contador-animado.directive';
import { FASES, JORNADA_EDITORIAL, RODAPE, SELOS } from './jornada.copy';
import { entradaDoCatalogo } from './jornada.catalogo';
import { JornadaResposta } from './jornada.model';
import { ListaDeMissoesComponent } from './lista-de-missoes.component';
import { MapaDeFasesComponent } from './mapa-de-fases.component';
import { PainelLateralComponent } from './painel-lateral.component';

/**
 * Jornada em três vistas: ação em foco, missões e conquistas exploráveis.
 * Recebe o estado do serviço e emite intenções; escolher um foco não altera
 * o progresso real. A API continua sendo a fonte de conquistas e XP.
 */
@Component({
  selector: 'mg-jornada-overlay',
  templateUrl: './jornada-overlay.component.html',
  styleUrls: ['./jornada-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkTrapFocus,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    CenaOrbitalComponent,
    ContadorAnimadoDirective,
    MapaDeFasesComponent,
    ListaDeMissoesComponent,
    PainelLateralComponent,
  ],
})
export class JornadaOverlayComponent {
  @Input({ required: true }) dados!: Signal<JornadaResposta | null>;
  @Input({ required: true }) carregando!: Signal<boolean>;
  @Input({ required: true }) idsNovos!: Signal<string[]>;
  /**
   * CV-CLI-31 — repasse: a lista precisa saber qual missão está gravando para
   * girar o spinner no botão certo. Opcional (default `null`) porque quem
   * monta o overlay sem o serviço — os specs — não tem escrita em voo.
   */
  @Input() excecaoEmAndamento: Signal<string | null> = signal(null);

  @Output() readonly fechar = new EventEmitter<void>();
  @Output() readonly irPara = new EventEmitter<string>();
  @Output() readonly naoSeAplica = new EventEmitter<{ id: string; justificativa: string }>();
  @Output() readonly removerExcecao = new EventEmitter<string>();

  private readonly permissoes = inject(PermissionService);
  private readonly elemento = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly abas = [
    { id: 'passo', titulo: 'Próximo passo' },
    { id: 'missoes', titulo: 'Missões' },
    { id: 'conquistas', titulo: 'Conquistas' },
  ] as const;
  protected readonly aba = signal('passo');
  protected readonly movimentoPausado = signal(false);
  private readonly focoEscolhido = signal<string | null>(null);
  protected readonly FASES = FASES;
  protected readonly SELOS = SELOS;
  protected readonly RODAPE = RODAPE;

  /** `null` até o dado chegar; depois, a fase atual — e o que o usuário clicar. */
  private readonly escolhida = signal<number | null>(null);
  protected readonly faseSelecionada = computed(() => this.escolhida() ?? this.dados()?.fase_atual ?? 1);
  protected readonly faseCopy = computed(() => FASES[this.faseSelecionada() - 1]);
  protected readonly missoesDaFase = computed(() => this.dados()?.fases.find(f => f.numero === this.faseSelecionada())?.missoes ?? []);
  protected readonly editorial = computed(() => JORNADA_EDITORIAL[this.faseSelecionada() - 1]);
  protected readonly progressoDaFase = computed(() => {
    const aplicaveis = this.missoesDaFase().filter(m => m.disponivel && m.estado !== 'nao_se_aplica');
    const concluidas = aplicaveis.filter(m => m.estado === 'concluida').length;
    return { total: aplicaveis.length, concluidas, restantes: aplicaveis.length - concluidas };
  });
  protected readonly faseConquistada = computed(() =>
    this.progressoDaFase().total > 0 && this.progressoDaFase().restantes === 0,
  );
  protected readonly missaoEmFoco = computed(() => {
    const candidatas = this.missoesDaFase().filter(m =>
      m.disponivel && !['concluida', 'nao_se_aplica', 'em_breve'].includes(m.estado),
    );
    const missao = candidatas.find(m => m.id === this.focoEscolhido())
      ?? candidatas.find(m => m.id === this.dados()?.proxima)
      ?? candidatas[0];
    return missao ? { ...missao, entrada: entradaDoCatalogo(missao.id) } : null;
  });
  protected readonly novasConquistas = computed(() =>
    this.dados()?.fases.flatMap(f => f.missoes).filter(m =>
      this.idsNovos().includes(m.id) && m.disponivel && m.estado === 'concluida',
    ) ?? [],
  );
  protected readonly xpPercentual = computed(() => {
    const xp = this.dados()?.xp;

    return xp && xp.maximo > 0 ? Math.round((xp.atual / xp.maximo) * 100) : 0;
  });
  protected readonly fasesDaCena = computed<FaseDaCena[]>(() => {
    const dados = this.dados();

    return FASES.map(fase => {
      const resposta = dados?.fases.find(f => f.numero === fase.numero);
      const aplicaveis = resposta?.missoes.filter(m => m.disponivel && m.estado !== 'nao_se_aplica') ?? [];
      const concluidas = aplicaveis.filter(m => m.estado === 'concluida').length;

      return {
        numero: fase.numero,
        nome: fase.nome,
        concluida: resposta?.concluida ?? false,
        atual: (dados?.fase_atual ?? 1) === fase.numero,
        percentual: aplicaveis.length ? Math.round((concluidas / aplicaveis.length) * 100) : 0,
      };
    });
  });
  protected readonly reduzirMovimento = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Vira `true` depois do primeiro quadro pintado — é a "partida" da barra de XP. */
  private readonly primeiroQuadroPassou = signal(false);
  private readonly xpNaBarra = computed(() => (this.reduzirMovimento || this.primeiroQuadroPassou() ? this.xpPercentual() : 0));

  @HostBinding('style.--mg-jornada-xp')
  protected get larguraDaBarra(): string {
    return `${this.xpNaBarra()}%`;
  }

  /**
   * Lê o store de permissões da sessão pelo MESMO método síncrono do cabeçalho
   * (`PermissionService.checkPermissions`, que já trata o curinga `'*'`). O
   * nome do catálogo é um caso de `TenantPerm` — ou de `CommonPerm`, para a
   * tela de cargos (J23).
   */
  protected readonly podeVer = (permissao: string): boolean => {
    const valor: string | undefined =
      TenantPerm[permissao as keyof typeof TenantPerm] ?? CommonPerm[permissao as keyof typeof CommonPerm];

    return valor ? this.permissoes.checkPermissions([valor]) : false;
  };

  constructor() {
    afterNextRender(() => this.primeiroQuadroPassou.set(true));
  }

  protected abrirAba(id: string): void {
    this.aba.set(id);
    this.elemento.nativeElement.querySelector<HTMLButtonElement>(`#mg-aba-${id}`)?.focus();
  }

  protected focar(id: string): void {
    const missao = this.missoesDaFase().find(m => m.id === id);
    if (!missao?.disponivel || ['concluida', 'em_breve', 'nao_se_aplica'].includes(missao.estado)
      || !this.podeVer(entradaDoCatalogo(id).permissao)) {
      return;
    }
    this.focoEscolhido.set(id);
    this.aba.set('passo');
    this.elemento.nativeElement.querySelector<HTMLButtonElement>('#mg-aba-passo')?.focus();
  }

  protected navegarAbas(evento: KeyboardEvent, indice: number): void {
    let destino: number;
    if (evento.key === 'ArrowRight') destino = (indice + 1) % this.abas.length;
    else if (evento.key === 'ArrowLeft') destino = (indice + this.abas.length - 1) % this.abas.length;
    else if (evento.key === 'Home') destino = 0;
    else if (evento.key === 'End') destino = this.abas.length - 1;
    else return;
    evento.preventDefault();
    this.aba.set(this.abas[destino].id);
    const grupo = (evento.currentTarget as HTMLElement).parentElement;
    grupo?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[destino]?.focus();
  }

  protected selecionar(numero: number, focarPainel = false): void {
    const saiuDasConquistas = this.aba() === 'conquistas';
    this.escolhida.set(numero);
    this.focoEscolhido.set(null);
    this.aba.set('passo');
    if (focarPainel || saiuDasConquistas) this.elemento.nativeElement.querySelector<HTMLButtonElement>('#mg-aba-passo')?.focus();
  }
}
